// utils/uploadToCloudinary.js
// ✅ Frontend-only utility
// Compresses with browser-image-compression then uploads directly to Cloudinary (unsigned)
// Returns the secure_url string

import imageCompression from "browser-image-compression";

const CLOUD_NAME = process.env.REACT_APP_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_UPLOAD_PRESET; // unsigned preset from Cloudinary dashboard

const compressionOptions = {
  maxSizeMB: 0.5,        // compress to max 500KB
  maxWidthOrHeight: 1000, // resize to max 1000px
  useWebWorker: true,     // non-blocking — runs in background thread
  fileType: "image/jpeg",
};

/**
 * Compress a file and upload it directly to Cloudinary.
 * @param {File} file - Raw File object from antd Upload (originFileObj)
 * @param {string} folder - Cloudinary folder name e.g. "businesses", "stalls"
 * @returns {Promise<string>} - Cloudinary secure_url
 */
export const uploadToCloudinary = async (file, folder) => {
  // 1. Compress in browser using Web Worker (non-blocking)
  const compressed = await imageCompression(file, compressionOptions);

  // 2. Build FormData for Cloudinary unsigned upload
  const formData = new FormData();
  formData.append("file", compressed);
  formData.append("upload_preset", UPLOAD_PRESET);
  formData.append("folder", folder);

  // 3. Upload directly to Cloudinary
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
    {
      method: "POST",
      body: formData,
    }
  );

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error?.message || "Cloudinary upload failed");
  }

  const data = await res.json();
  return {
    url: data.secure_url,
    public_id: data.public_id,
  };
};

/**
 * Upload multiple files in parallel.
 * @param {File[]} files - Array of File objects
 * @param {string} folder - Cloudinary folder
 * @returns {Promise<string[]>} - Array of secure_urls
 */
export const uploadMultipleToCloudinary = async (files, folder) => {
  return Promise.all(files.map((file) => uploadToCloudinary(file, folder)));
};